import { useAuth } from '@/hooks/useAuth';

/**
 * Prefisso i18n del lessico del denaro, calcolato dal ruolo di chi sta guardando.
 *
 * Regola unica, valida ovunque: gli stessi numeri hanno UN nome solo, e a cambiare col
 * ruolo e' la persona, non il lessico. Le due letture degli stessi importi -- una per chi
 * paga, una per chi incassa -- erano cio' che faceva cambiare nome alla stessa cifra da
 * una pagina all'altra, ed e' per evitarlo che ogni foglia sta qui dentro. Dal 23/09/2026
 * il lato operativo dice "Incassato" dove diceva "Pagato": una parola sola, cambiata in un
 * punto solo, e cambiata ovunque.
 *
 *   traveler                      -> `money.self`   -- e' lui che paga, gli si parla in
 *                                    seconda persona ("Non hai ancora pagato...")
 *   admin, agency, TD, TA member  -> `money.client` -- guardano il conto di qualcun
 *                                    altro, quindi terza persona ("Nessun pagamento
 *                                    registrato finora.")
 *
 * Il prefisso e' tipizzato come unione delle due stringhe letterali: interpolarlo con una
 * foglia (`${money}.paid`) produce una `TranslationKey` valida senza cast, e una foglia
 * che manca in `money` non compila. In JSX si usa cosi':
 *
 *   const money = useMoneyCopy();
 *   <span>{t(`${money}.paid`)}</span>
 *
 * Ogni foglia va tenuta in ENTRAMBI i blocchi, in ENTRAMBE le lingue: e' il prezzo dello
 * sdoppiamento, ed e' anche cio' che permette di far divergere una frase senza rincorrere
 * i componenti che la usano.
 */
export type MoneyCopyKey = 'money.self' | 'money.client';

export function useMoneyCopy(): MoneyCopyKey {
    const { userRole } = useAuth();
    // Il default non e' `self`: un ruolo non ancora caricato e' quasi sempre un ruolo
    // operativo che sta montando la pagina, e dare del "tu" a un'agenzia si nota molto
    // piu' del contrario.
    return userRole === 'traveler' ? 'money.self' : 'money.client';
}
